import Swiper from 'swiper';
import { Pagination, Navigation, Autoplay, Mousewheel, A11y } from 'swiper/modules';
import type { SwiperOptions } from 'swiper/types';
import { attachEmulatedInfiniteWithoutLoop, type EmulatedInfiniteControl } from './carousel-emulate-infinite';
import { CAROUSEL_MOBILE_MAX_WIDTH_PX, isCarouselMobileViewport } from './carousel-viewport';

const AUTOPLAY_TOGGLE_CLASS = 'carousel-autoplay-toggle';

export interface AutoplayConfig {
  delay: number;
  disableOnInteraction: boolean;
}

export interface BreakpointConfig {
  slidesPerView?: number;
  spaceBetween?: number;
  loop?: boolean;
}

export interface BaseCarouselConfig {
  slidesPerView: number;
  spaceBetween: number;
  loop: boolean;
  speed?: number;
  autoHeight?: boolean;
  autoplay?: AutoplayConfig | false;
  pagination: boolean;
  navigation: boolean;
  breakpoints?: Record<number, BreakpointConfig>;
  mousewheel?: boolean;
  centeredSlides?: boolean;
}

export interface CarouselInstance {
  container: HTMLElement;
  swiperInstance: Swiper | null;
  config: BaseCarouselConfig;
  /** Clears matchMedia listener registered when config.loop is true */
  mqlCleanup?: () => void;
  /** Survives re-initialisation so a viewport change doesn't restart a carousel the visitor paused */
  autoplayPausedByUser?: boolean;
}

export abstract class BaseCarousel {
  protected readonly carousels: Map<HTMLElement, CarouselInstance> = new Map();
  protected abstract getDefaultConfig(): BaseCarouselConfig;

  public init(selector: string, customConfig?: Partial<BaseCarouselConfig>): void {
    const containers = document.querySelectorAll(selector);
    
    if (containers.length === 0) {
      return;
    }

    const config = customConfig 
      ? { ...this.getDefaultConfig(), ...customConfig }
      : this.getDefaultConfig();
    
    containers.forEach(container => {
      const element = container as HTMLElement;
      this.carousels.set(element, {
        container: element,
        swiperInstance: null,
        config: config
      });
      this.initCarousel(element, selector);
    });
  }

  protected initCarousel(container: HTMLElement, selector: string): void {
    const carouselInstance = this.carousels.get(container);
    if (!carouselInstance) return;

    this.destroyCarousel(carouselInstance);
    this.addSwiperStructure(container, carouselInstance.config);
    this.createSwiperInstance(carouselInstance, selector);
  }

  protected destroyCarousel(carouselInstance: CarouselInstance): void {
    carouselInstance.mqlCleanup?.();
    carouselInstance.mqlCleanup = undefined;
    if (carouselInstance.swiperInstance) {
      carouselInstance.swiperInstance.destroy();
      carouselInstance.swiperInstance = null;
    }
  }

  protected addSwiperStructure(container: HTMLElement, config: BaseCarouselConfig): void {
    if (container.classList.contains('swiper')) {
      return;
    }

    container.classList.add('swiper');

    this.createSwiperWrapper(container);
    this.addSwiperControls(container, config);
  }

  protected createSwiperWrapper(container: HTMLElement): void {
    const wrapper = document.createElement('div');
    wrapper.className = 'swiper-wrapper';

    // Move existing content to wrapper
    while (container.firstChild) {
      wrapper.appendChild(container.firstChild);
    }
    container.appendChild(wrapper);

    // Add swiper-slide class to all direct children
    const slides = wrapper.querySelectorAll(':scope > *');
    slides.forEach(slide => {
      (slide as HTMLElement).classList.add('swiper-slide');
    });
  }

  protected addSwiperControls(container: HTMLElement, config: BaseCarouselConfig): void {
    if (config.pagination) {
      const pagination = document.createElement('div');
      pagination.className = 'swiper-pagination';
      container.appendChild(pagination);
    }

    if (config.navigation) {
      const prevButton = document.createElement('button');
      prevButton.type = 'button';
      prevButton.className = 'swiper-button-prev';
      container.appendChild(prevButton);

      const nextButton = document.createElement('button');
      nextButton.type = 'button';
      nextButton.className = 'swiper-button-next';
      container.appendChild(nextButton);
    }

    if (config.autoplay) {
      const autoplayToggle = document.createElement('button');
      autoplayToggle.type = 'button';
      autoplayToggle.className = AUTOPLAY_TOGGLE_CLASS;
      container.appendChild(autoplayToggle);
    }
  }

  protected removeSwiperStructure(container: HTMLElement): void {
    if (!container.classList.contains('swiper')) {
      return;
    }

    container.classList.remove('swiper', 'swiper-container');
    this.restoreOriginalStructure(container);
    this.removeSwiperControls(container);
  }

  protected restoreOriginalStructure(container: HTMLElement): void {
    const wrapper = container.querySelector('.swiper-wrapper');
    if (wrapper) {
      while (wrapper.firstChild) {
        container.appendChild(wrapper.firstChild);
      }
      wrapper.remove();
    }

    // Remove swiper classes from slides
    const slides = container.querySelectorAll('.swiper-slide');
    slides.forEach(slide => slide.classList.remove('swiper-slide'));
  }

  protected removeSwiperControls(container: HTMLElement): void {
    const pagination = container.querySelector('.swiper-pagination');
    pagination?.remove();

    const prevButton = container.querySelector('.swiper-button-prev');
    prevButton?.remove();

    const nextButton = container.querySelector('.swiper-button-next');
    nextButton?.remove();

    const autoplayToggle = container.querySelector(`:scope > .${AUTOPLAY_TOGGLE_CLASS}`);
    autoplayToggle?.remove();
  }

  protected createSwiperInstance(carouselInstance: CarouselInstance, selector: string): void {
    const container = carouselInstance.container;
    const config = carouselInstance.config;
    const swiperConfig = this.buildSwiperConfig(container, config);

    const swiper = new Swiper(container, swiperConfig);
    carouselInstance.swiperInstance = swiper;

    let emulatedInfinite: EmulatedInfiniteControl | undefined;
    if (config.loop && isCarouselMobileViewport()) {
      emulatedInfinite = attachEmulatedInfiniteWithoutLoop(swiper, {
        navigation: config.navigation,
        autoplayDelayMs:
          config.autoplay && config.autoplay.delay > 0 ? config.autoplay.delay : false,
        disableAutoplayOnInteraction: config.autoplay ? config.autoplay.disableOnInteraction : undefined,
      });
    }

    if (config.loop) {
      const mql = window.matchMedia(`(max-width: ${CAROUSEL_MOBILE_MAX_WIDTH_PX}px)`);
      const onLoopViewportChange = () => this.initCarousel(container, selector);
      mql.addEventListener('change', onLoopViewportChange);
      carouselInstance.mqlCleanup = () => mql.removeEventListener('change', onLoopViewportChange);
    }

    this.attachAutoplayToggle(carouselInstance, emulatedInfinite);
    this.attachCustomEventHandlers(carouselInstance, selector);
  }

  private attachAutoplayToggle(
    carouselInstance: CarouselInstance,
    emulatedInfinite: EmulatedInfiniteControl | undefined
  ): void {
    const swiper = carouselInstance.swiperInstance;
    const toggle = carouselInstance.container.querySelector<HTMLButtonElement>(`:scope > .${AUTOPLAY_TOGGLE_CLASS}`);
    if (!swiper || !toggle) return;

    const setPaused = (paused: boolean) => {
      carouselInstance.autoplayPausedByUser = paused;
      if (emulatedInfinite) {
        emulatedInfinite.setPaused(paused);
      } else if (paused) {
        swiper.autoplay?.stop();
      } else {
        swiper.autoplay?.start();
      }
      toggle.setAttribute('aria-label', paused ? 'Play carousel' : 'Pause carousel');
      toggle.classList.toggle(`${AUTOPLAY_TOGGLE_CLASS}--paused`, paused);
      // Announce slide changes only while the visitor is in control.
      swiper.wrapperEl.setAttribute('aria-live', paused ? 'polite' : 'off');
    };
    const onClick = () => setPaused(!carouselInstance.autoplayPausedByUser);

    setPaused(Boolean(carouselInstance.autoplayPausedByUser));
    toggle.addEventListener('click', onClick);
    swiper.on('destroy', () => toggle.removeEventListener('click', onClick));
  }

  protected attachCustomEventHandlers(_carouselInstance: CarouselInstance, _selector: string): void {
    // Override in subclasses to add custom event handlers
  }

  protected getSwiperModules(config: BaseCarouselConfig): any[] {
    const modules = [A11y];

    if (config.pagination) {
      modules.push(Pagination);
    }
    
    if (config.navigation) {
      modules.push(Navigation);
    }
    
    const emulatedInfiniteAutoplay = Boolean(
      config.loop && config.autoplay && isCarouselMobileViewport()
    );
    if (config.autoplay && !emulatedInfiniteAutoplay) {
      modules.push(Autoplay);
    }

    if (config.mousewheel) {
      modules.push(Mousewheel);
    }
    
    return modules;
  }

  protected buildSwiperConfig(container: HTMLElement, config: BaseCarouselConfig): SwiperOptions {
    const modules = this.getSwiperModules(config);
    const emulatedInfiniteAutoplay = Boolean(
      config.loop && config.autoplay && isCarouselMobileViewport()
    );

    const swiperConfig: SwiperOptions = {
      modules,
      slidesPerView: config.slidesPerView,
      spaceBetween: config.spaceBetween,
      loop: false,
      // A slide role would replace the listitem role of <li> slides in the card and tour lists.
      // Swiper skips a falsy slideRole; '' rather than null keeps the type a string.
      a11y: {
        slideRole: container.querySelector('.swiper-slide')?.tagName === 'LI' ? '' : 'group',
      },
    };

    if (config.speed !== undefined) {
      swiperConfig.speed = config.speed;
    }

    if (config.autoHeight !== undefined) {
      swiperConfig.autoHeight = config.autoHeight;
    }

    if (config.centeredSlides !== undefined) {
      swiperConfig.centeredSlides = config.centeredSlides;
    }

    const tabletMin = 768;
    if (config.loop) {
      const breakpoints = { ...(config.breakpoints || {}) };
      const numericKeys = Object.keys(breakpoints).map(Number);
      numericKeys
        .filter((k) => k >= tabletMin)
        .forEach((k) => {
          breakpoints[k] = { ...breakpoints[k], loop: true };
        });
      if (!numericKeys.includes(tabletMin)) {
        breakpoints[tabletMin] = {
          ...(breakpoints[tabletMin] || {}),
          loop: true,
        };
      }
      swiperConfig.breakpoints = breakpoints;
    } else if (config.breakpoints) {
      swiperConfig.breakpoints = { ...config.breakpoints };
    }

    if (config.pagination) {
      const paginationElement = container.querySelector('.swiper-pagination') as HTMLElement;
      if (paginationElement) {
        swiperConfig.pagination = {
          el: paginationElement,
          clickable: true,
        };
      }
    }

    if (config.navigation) {
      swiperConfig.navigation = {
        nextEl: container.querySelector('.swiper-button-next') as HTMLElement,
        prevEl: container.querySelector('.swiper-button-prev') as HTMLElement,
      };
    }

    if (config.autoplay && !emulatedInfiniteAutoplay) {
      swiperConfig.autoplay = {
        ...config.autoplay,
        pauseOnMouseEnter: false,
        disableOnInteraction: false,
      };
    }

    if (config.mousewheel) {
      swiperConfig.mousewheel = {
        forceToAxis: true,
        sensitivity: 1,
        releaseOnEdges: false,
        thresholdDelta: 50,
        thresholdTime: 500,
      };
    }

    return swiperConfig;
  }

  public destroy(): void {
    this.carousels.forEach((carouselInstance) => {
      this.destroyCarousel(carouselInstance);
    });
    this.carousels.clear();
  }
}

